import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { ArrowLeft, TrendingUp, Target, Tag, CheckCircle2, Briefcase, DollarSign } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useConvexProjects } from "../../../services/convexService";
import CurrencyDisplay from "../../../components/currency/CurrencyDisplay";

export function ProjectsPreview() {
  const [isVisible, setIsVisible] = useState(false);
  const [animatedProgress, setAnimatedProgress] = useState<number[]>([0, 0, 0]);
  const sectionRef = useRef<HTMLElement>(null);
  const { t, language } = useLanguage();
  const { getProjectsByStatus } = useConvexProjects();

  // Fetch approved projects from database
  const approvedProjects = getProjectsByStatus("approved");

  // Filter to show both investment and sale projects
  const filteredProjects = approvedProjects
    ? approvedProjects.filter(project =>
      project.projectPurpose === "getInvestment" || project.projectPurpose === "sell"
    )
    : [];

  // Check if we have at least 3 projects to show
  const shouldShow = filteredProjects && filteredProjects.length >= 3;

  useEffect(() => {
    if (!shouldShow) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Animate progress rings
          setTimeout(() => {
            setAnimatedProgress(filteredProjects.slice(0, 3).map(p => {
              if (p.projectPurpose === "sell") {
                return 100;
              } else {
                const progress = Math.min(Math.round((p.currentFunding / p.fundingGoal) * 100), 100);
                return progress || 0;
              }
            }));
          }, 500);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [shouldShow, filteredProjects]);

  if (!shouldShow) {
    return null;
  }

  const projectsToShow = filteredProjects.slice(0, 3);

  // Circle SVG parameters
  const circleRadius = 70;
  const circumference = 2 * Math.PI * circleRadius;

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-20 overflow-hidden" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className={`flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div>
            <span className="text-sm font-semibold text-primary uppercase tracking-wide">
              {t('public.projects.title')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-3 mb-2">
              {t('public.projects.subtitle')}
            </h2>
            <p className="text-muted-foreground">
              <strong>{t('public.projects.description').split('<br/>')[0]}</strong><br />
              {t('public.projects.description').split('<br/>')[1]}
            </p>
          </div>
          <Button asChild variant="outline" className="gap-2 hover-lift group">
            <Link to="/projects">
              {t('public.projects.viewAll')}
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
            </Link>
          </Button>
        </div>

        {/* Dark Petrol Top Section with Circular Cards */}
        <div className={`rounded-3xl overflow-hidden transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Top: Dark petrol background with circles */}
          <div className="bg-hero-gradient relative px-4 sm:px-8 pt-10 pb-14 md:pt-14 md:pb-20">
            {/* Decorative blurred circles */}
            <div className="absolute top-0 left-1/4 w-40 h-40 bg-secondary/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-56 h-56 bg-secondary/5 rounded-full blur-3xl" />

            <div className="relative z-10 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-10 justify-items-center">
              {projectsToShow.map((project, index) => {
                let progress: number;
                if (project.projectPurpose === "sell") {
                  progress = 100;
                } else {
                  progress = Math.min(Math.round((project.currentFunding / project.fundingGoal) * 100), 100);
                }

                const animProgress = animatedProgress[index];
                const strokeDashoffset = circumference - (circumference * animProgress) / 100;

                // Size classes for the middle card (slightly larger like in the reference)
                const isMiddle = index === 1;
                const circleSize = isMiddle ? 'w-44 h-44 sm:w-52 sm:h-52' : 'w-36 h-36 sm:w-44 sm:h-44';
                const svgSize = isMiddle ? 180 : 160;

                return (
                  <Link
                    key={project._id}
                    to={`/projects/${project._id}`}
                    className={`flex flex-col items-center gap-4 group cursor-pointer transition-all duration-700 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}
                    style={{ transitionDelay: `${index * 200}ms` }}
                  >
                    {/* Circular Ring with Progress */}
                    <div className={`${circleSize} relative flex items-center justify-center group-hover:scale-105 transition-transform duration-500`}>
                      {/* Outer decorative ring */}
                      <div className="absolute inset-0 rounded-full border-[3px] border-secondary/20" />

                      {/* SVG Progress Ring */}
                      <svg
                        className="absolute inset-0 -rotate-90"
                        width="100%"
                        height="100%"
                        viewBox={`0 0 ${svgSize} ${svgSize}`}
                      >
                        {/* Background circle */}
                        <circle
                          cx={svgSize / 2}
                          cy={svgSize / 2}
                          r={circleRadius}
                          fill="none"
                          stroke="hsl(173 78% 40% / 0.15)"
                          strokeWidth="5"
                        />
                        {/* Progress circle */}
                        <circle
                          cx={svgSize / 2}
                          cy={svgSize / 2}
                          r={circleRadius}
                          fill="none"
                          stroke="hsl(173 78% 40%)"
                          strokeWidth="5"
                          strokeLinecap="round"
                          strokeDasharray={circumference}
                          strokeDashoffset={strokeDashoffset}
                          className="transition-all duration-[1500ms] ease-out"
                        />
                      </svg>

                      {/* Inner content */}
                      <div className="relative z-10 flex flex-col items-center text-center px-4">
                        <span className="text-primary-foreground/70 text-xs sm:text-sm font-medium mb-1">
                          {project.category}
                        </span>
                        <span className="text-primary-foreground text-lg sm:text-xl font-bold leading-tight line-clamp-2">
                          {project.title}
                        </span>
                      </div>
                    </div>

                    {/* Badge below circle */}
                    <div className="flex items-center gap-2">
                      {project.projectPurpose === "sell" ? (
                        <Badge className="bg-secondary/20 text-secondary border-secondary/30 hover:bg-secondary/30 text-xs">
                          <Tag className="w-3 h-3 mr-1" />
                          {t('projectPurpose.sellTag')}
                        </Badge>
                      ) : (
                        <Badge className="bg-secondary/20 text-secondary border-secondary/30 hover:bg-secondary/30 text-xs">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          {animProgress}%
                        </Badge>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Bottom: Light section with details */}
          <div className="bg-card border border-t-0 border-border rounded-b-3xl px-4 sm:px-8 py-8 md:py-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-10">
              {projectsToShow.map((project, index) => {

                return (
                  <div
                    key={project._id}
                    className={`text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                    style={{ transitionDelay: `${300 + index * 150}ms` }}
                  >
                    {/* Section title */}
                    <h3 className="text-lg font-bold mb-4 text-foreground">
                      {project.title}
                    </h3>

                    {/* Detail items with checkmarks */}
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-secondary flex-shrink-0" />
                        <span>{project.description.length > 60 ? project.description.substring(0, 60) + '...' : project.description}</span>
                      </div>
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-secondary flex-shrink-0" />
                        <span>
                          {project.projectPurpose === "sell"
                            ? t('projectSale.amountLabel')
                            : t('public.projects.target')}: <CurrencyDisplay amount={project.fundingGoal} />
                        </span>
                      </div>
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-secondary flex-shrink-0" />
                        <span>{project.category}</span>
                      </div>
                    </div>

                    {/* Action button */}
                    <Button
                      className="mt-5 bg-hero-gradient hover:opacity-90 text-primary-foreground text-xs px-6 rounded-full"
                      size="sm"
                      asChild
                    >
                      <Link to={`/projects/${project._id}`}>
                        {project.projectPurpose === "sell"
                          ? t('projectSale.buyProject')
                          : t('public.projects.investNow')}
                      </Link>
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}